<template>
  <div class="search-form">
    <h1 class="search-page__headline headline-large">
      {{ titleParts.firstPart }}
      <span class="search-page__headline--highlighted">{{ titleParts.lastPart }}</span>
    </h1>
    <section class="search-page">
      <div class="search-page__top-row">
        <section class="search-page__container">
          <section class="search-page__search">
            <SearchOrigin />
            <SearchDestination />
            <DirectionButton />
          </section>
        </section>
        <div class="search-page__calendar-desktop">
          <TravelDate />
          <ReturnDate />
        </div>
      </div>
      <div class="search-page__calendar-mobile">
        <TravelDate />
        <ReturnDate />
      </div>
      <div class="search-page__bottom-row">
        <SearchPassengers class="search-page__passengers" />
        <button
          class="search-page__continue"
          :disabled="!canProceed"
          @click="handleSubmit">
          {{ $t('search.continue') }}
        </button>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useTravelSearchStore } from '@/stores/travelSearchStore'
  import { fetchStations } from '@/services/journeyService'
  import { pushOrderFlowToDatalayer } from '@/services/gaLoggingService'
  import { Station } from '@/types/stationTypes'
  import { parseSearchPrefillQuery } from '@/utils/searchQueryPrefill'
  import {
    serializePassengersToUrl,
    parsePassengersFromUrl,
    generatePassengerId,
  } from '@/utils/passengerUrlUtils'
  import SearchOrigin from '@/components/TravelPlanner/SearchOrigin.vue'
  import SearchDestination from '@/components/TravelPlanner/SearchDestination.vue'
  import TravelDate from '@/components/TravelPlanner/TravelDate.vue'
  import ReturnDate from '@/components/TravelPlanner/ReturnDate.vue'
  import DirectionButton from '@/components/TravelPlanner/DirectionButton.vue'
  import SearchPassengers from '@/components/TravelPlanner/PassengerPicker/SearchPassengers.vue'
  import { t } from '@/i18n'

  // Store setup
  const travelSearchStore = useTravelSearchStore()

  const { OriginStation, destinationStation, departureDate, returnDate, passengers } =
    storeToRefs(travelSearchStore)

  const canProceed = computed(() => {
    return !!OriginStation.value?.name && !!destinationStation.value?.name && !!departureDate.value
  })

  const titleParts = computed(() => {
    const title = t('search.title').trim()
    const words = title.split(/\s+/)

    return {
      lastPart: words.pop() || '',
      firstPart: words.join(' '),
    }
  })

  const findStationByQueryValue = (
    stations: Station[],
    queryValue: string | undefined,
  ): Station | undefined => {
    if (!queryValue) return undefined

    const normalizedValue = queryValue.trim().toLowerCase()
    return stations.find(
      (station) =>
        station._u_i_c_station_code.toLowerCase() === normalizedValue ||
        station.name.toLowerCase() === normalizedValue,
    )
  }

  // Seed passengers from a `?passengers=` param, otherwise default to one adult.
  // Done here (not in SearchPassengers) so the summary + redirect URL are correct
  // before the picker is ever opened.
  const seedPassengers = (rawPassengers: string | undefined) => {
    if (rawPassengers) {
      try {
        const parsed = parsePassengersFromUrl(rawPassengers)
        if (parsed.length > 0) {
          travelSearchStore.setPassengers(parsed)
          return
        }
      } catch (error) {
        console.warn('Failed to parse passengers from query:', rawPassengers, error)
      }
    }
    travelSearchStore.setPassengers([
      { id: generatePassengerId(0), type: 'ADT', discount_cards: [] },
    ])
  }

  const applyQueryPrefill = async () => {
    const parsedQuery = parseSearchPrefillQuery(window.location.search)

    if (parsedQuery.errors.length) {
      parsedQuery.errors.forEach((error) => {
        console.warn(error)
      })
    }

    travelSearchStore.reset()

    const rawPassengers = new URLSearchParams(window.location.search).get('passengers') || undefined
    seedPassengers(rawPassengers)

    if (!parsedQuery.hasPrefillValues) {
      return
    }

    if (parsedQuery.outboundDate) {
      travelSearchStore.setDepartureDate(parsedQuery.outboundDate)
    }

    travelSearchStore.setReturnDate(parsedQuery.inboundDate ?? null)

    if (!parsedQuery.origin && !parsedQuery.destination) {
      return
    }

    if (!travelSearchStore.allStations.length) {
      const fetchedStations = await fetchStations()
      travelSearchStore.setAllStations(fetchedStations)
    }

    const originStation = findStationByQueryValue(travelSearchStore.allStations, parsedQuery.origin)
    if (originStation) {
      await travelSearchStore.setOriginStation({
        name: originStation.name,
        _u_i_c_station_code: originStation._u_i_c_station_code,
      })
    }

    const destinationStation = findStationByQueryValue(
      travelSearchStore.allStations,
      parsedQuery.destination,
    )

    if (destinationStation) {
      travelSearchStore.setDestinationStation({
        name: destinationStation.name,
        _u_i_c_station_code: destinationStation._u_i_c_station_code,
      })
      travelSearchStore.setOriginDestination(
        travelSearchStore.OriginStation.name,
        destinationStation.name,
      )
    }
  }

  const handleSubmit = () => {
    const query: Record<string, string> = {
      origin: OriginStation.value?._u_i_c_station_code || OriginStation.value?.name || '',
      destination:
        destinationStation.value?._u_i_c_station_code || destinationStation.value?.name || '',
      outboundDate: departureDate.value || '',
      fromLight: 'true',
    }

    if (returnDate.value) {
      query.returnDate = returnDate.value
    }

    if (passengers.value.length) {
      query.passengers = serializePassengersToUrl(passengers.value)
    }

    // Redirect to SPA
    const queryString = new URLSearchParams(query).toString()
    const spaBaseUrl = import.meta.env.VITE_SPA_BASE_URL
    window.location.href = `${spaBaseUrl}/?${queryString}`
  }

  onMounted(async () => {
    try {
      await applyQueryPrefill()
      pushOrderFlowToDatalayer(1, 'Start search', { order_type: 'New order' })
    } catch (error) {
      console.error('Error initializing SearchForm:', error)
    }
  })
</script>

<style scoped lang="scss">
  // Mirror S3's `.search-page__inner`: stack the headline above the card with a
  // fixed gap so the title isn't cramped against it.
  .search-form {
    width: 100%;
    max-width: rem(1024);
    margin: 0 auto;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: rem(32);
  }

  .search-page {
    position: relative;
    margin: 0 auto;
    padding-left: rem(8);
    padding-right: rem(8);
    width: 100%;
    max-width: rem(390);
    box-sizing: border-box; // keep padding inside width:100% (no global border-box reset)
    background-color: transparent;
    color: $clr-text;
    border-radius: $border-card-radius;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: rem(12);

    @media (min-width: $breakpoint-xxs) {
      padding: rem(16);
      background-color: $clr-white;
      box-shadow: $box-shadow-card-large;

      &__passengers {
        min-width: rem(220);
      }
    }

    & > * {
      flex: 1 1 auto;
    }

    @media (min-width: $breakpoint-lg) {
      gap: rem(18);
      padding: rem(24);
      max-width: rem(1024);
    }

    &__headline {
      position: relative;
      z-index: 1;
      width: 100%;
      max-width: rem(1024);
      margin: 0 auto;
      text-align: center;

      @media (min-width: $breakpoint-lg) {
        text-align: left;
      }

      &--highlighted {
        color: $clr-secondary;
      }

      &--mobile {
        width: auto;
        margin: 0 rem(32);
      }
    }

    &__top-row {
      display: flex;
      flex-direction: column;
      gap: rem(8);

      // Row 1 lays the search inputs and the date pickers side by side. Row 2 is a
      // separate flex row (see __bottom-row), so the two rows size independently.
      @media (min-width: $breakpoint-lg) {
        flex-direction: row;
        align-items: stretch;
      }
    }

    &__container {
      display: flex;
      flex-direction: column;
      align-items: stretch;

      @media (min-width: $breakpoint-lg) {
        flex: 1;
        min-width: 0;
      }
    }

    &__search {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      border: 1px solid $clr-stroke;
      border-radius: $border-radius;
      background-color: $clr-white;

      // Fix the field height here and stretch the input to fill it, so the
      // value sits single-line and vertically centred (overrides the base
      // Input's taller container + internal-label padding).
      :deep(.input__container) {
        min-height: $search-field-height;
        align-items: stretch;
      }

      :deep(.input__field) {
        @include search-field-surface;
        padding: 0 $search-field-padding-x;

        // Origin/destination always have a leading icon — reserve room for it.
        &:has(+ .input__icon) {
          padding-left: $search-field-padding-x + $search-field-icon-size + $search-field-icon-gap;
        }
      }

      // Neutralise the two-line label padding so the value stays centred.
      :deep(.input__field--internal-label) {
        padding-top: 0;
        padding-bottom: 0;
      }

      // Base Input pins the suggestions list at a fixed height tuned for the taller
      // default field. Track the shortened field height so the dropdown sits flush.
      :deep(.input__suggestions-list) {
        top: $search-field-height;
      }

      :deep(.input__icon) {
        @include search-field-icon;
        left: $search-field-padding-x;
        top: 50%;
        transform: translateY(-50%);
      }

      :deep(.input__internal-label) {
        @include search-field-label;

        &:has(+ .input__field + .input__icon) {
          left: rem(10);
        }
      }

      :deep(.search-origin .input__field) {
        border-radius: $border-radius $border-radius 0 0;
      }

      :deep(.search-destination .input__field) {
        border-radius: 0 0 $border-radius $border-radius;
      }

      :deep(.search-destination) {
        border-top: 1px solid $clr-stroke;
      }

      :deep(.direction-button) {
        top: 50%;
        transform: translateY(-50%) rotate(90deg);
        z-index: 9;
      }

      // Light's Input uses a `&times;` text button (not S3's inner SVG), so offset
      // the glyph from the field's right edge with padding-right to mirror S3's
      // close-icon margin. Stacked: clear the direction button sitting on the divider.
      :deep(.input__delete-button) {
        right: 0;
        padding-right: rem(46);
        align-self: center;
      }

      @media (min-width: $breakpoint-lg) {
        flex-direction: row;

        :deep(.search-origin),
        :deep(.search-destination) {
          flex: 1;
          min-width: 0;
        }

        :deep(.search-origin .input__field) {
          border-radius: $border-radius 0 0 $border-radius;
        }

        :deep(.search-destination .input__field) {
          border-radius: 0 $border-radius $border-radius 0;
        }

        :deep(.search-destination) {
          border-top: none;
          border-left: 1px solid $clr-stroke;
        }

        :deep(.direction-button) {
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
        }

        :deep(.input__delete-button) {
          padding-right: rem(28);
        }

        // The 32px direction button is centred over the origin/destination divider
        // on desktop, overlapping ~16px into the destination field. Offset the
        // destination icon + value so they clear it (12px gap to the marker icon).
        $dest-icon-left: rem(28); // 16px button half-width + 12px gap

        :deep(.search-destination .input__icon) {
          left: $dest-icon-left;
        }

        :deep(.search-destination .input__field:has(+ .input__icon)) {
          padding-left: calc(
            #{$dest-icon-left} + #{$search-field-icon-size} + #{$search-field-icon-gap}
          );
        }
      }
    }

    &__calendar-mobile {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      flex: 1 1 auto;
      border: 1px solid $clr-stroke;
      border-radius: $border-radius;

      & > * {
        flex: 1;
        min-width: 0;
      }

      :deep(.selected-date) {
        min-width: 0;
        width: 100%;
      }

      & > :first-child :deep(.selected-date) {
        border-radius: $border-radius 0 0 $border-radius;
      }

      & > :last-child :deep(.selected-date) {
        border-radius: 0 $border-radius $border-radius 0;
      }

      :deep(.return-date-picker) {
        border-left: 1px solid $clr-stroke;
      }

      @media (min-width: $breakpoint-lg) {
        display: none;
      }
    }

    &__calendar-desktop {
      display: none;
      @media (min-width: $breakpoint-lg) {
        display: flex;
        flex-direction: row;
        flex: 0 0 rem(300);
        border: 1px solid $clr-stroke;
        border-radius: $border-radius;

        & > * {
          flex: 1;
        }

        :deep(.selected-date) {
          width: 100%;
        }

        & > :first-child :deep(.selected-date) {
          border-radius: $border-radius 0 0 $border-radius;
        }

        & > :last-child :deep(.selected-date) {
          border-radius: 0 $border-radius $border-radius 0;
        }

        :deep(.return-date-picker) {
          border-left: 1px solid $clr-stroke;
        }
      }
    }

    &__bottom-row {
      display: flex;
      flex-direction: column;
      gap: rem(12);

      // Row 2 is its own full-width flex row, independent of row 1's sizing, so the
      // passenger button sizes to its content and Fortsett isn't cramped.
      @media (min-width: $breakpoint-lg) {
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        gap: rem(8);
      }
    }

    &__continue {
      width: 100%;
      box-sizing: border-box;
      min-height: $search-field-height;
      padding: 0 rem(40);
      background-color: $clr-primary;
      color: $clr-white;
      border: none;
      border-radius: $border-radius;
      cursor: pointer;
      // Buttons don't inherit the page font (UA default = Arial); pull in Ginto.
      font-family: inherit;
      font-size: rem(18);
      font-weight: 500;
      line-height: rem(24);
      text-align: center;

      // Disabled state: a faded primary button (clr-primary at 35% opacity)
      // instead of the default grey disabled styling.
      &:disabled {
        background-color: rgba($clr-primary, 0.35);
        border-color: rgba($clr-primary, 0.35);
        color: $clr-white;
        cursor: not-allowed;
      }

      @media (min-width: $breakpoint-lg) {
        width: auto;
        min-width: rem(120);
      }
    }
  }
</style>
